import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import {
  Hourglass, RefreshCw, Plus, Trash2, MailCheck, Clock4,
  X, AlertTriangle, CheckCircle2, Send,
} from "lucide-react";

const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const STATUS = {
  waiting: { label: "In attesa", bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-900/50", text: "text-amber-800 dark:text-amber-300", dot: "bg-amber-500" },
  notified: { label: "Notificato", bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-200 dark:border-blue-900/50", text: "text-blue-800 dark:text-blue-300", dot: "bg-blue-500" },
  booked: { label: "Prenotato", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-900/50", text: "text-emerald-800 dark:text-emerald-300", dot: "bg-emerald-500" },
  expired: { label: "Scaduto", bg: "bg-zinc-50 dark:bg-zinc-800/60", border: "border-zinc-200 dark:border-zinc-700", text: "text-zinc-700 dark:text-zinc-300", dot: "bg-zinc-400" },
  cancelled: { label: "Annullato", bg: "bg-zinc-50 dark:bg-zinc-800/60", border: "border-zinc-200 dark:border-zinc-700", text: "text-zinc-700 dark:text-zinc-300", dot: "bg-zinc-400" },
};

function fmtDate(iso) { if (!iso) return "—"; return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
function fmtDt(iso) { if (!iso) return "—"; return new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
function minToHHMM(m) {
  if (m == null) return "";
  const h = Math.floor(m / 60), mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
function HHMMtoMin(s) {
  if (!s) return null;
  const [h, m] = s.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
}

const Kpi = ({ label, value, icon: Icon, accent }) => {
  const ring = {
    blue: "from-blue-500/10 to-blue-500/0 ring-blue-500/20",
    amber: "from-amber-500/10 to-amber-500/0 ring-amber-500/20",
    emerald: "from-emerald-500/10 to-emerald-500/0 ring-emerald-500/20",
    zinc: "from-zinc-500/10 to-zinc-500/0 ring-zinc-500/20",
  }[accent];
  const text = { blue: "text-blue-600", amber: "text-amber-600", emerald: "text-emerald-600", zinc: "text-zinc-600" }[accent];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${ring} ring-1 p-5`}>
      <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">
        <Icon className={`h-3.5 w-3.5 ${text}`} /> {label}
      </div>
      <div className="font-display text-3xl mt-2 tabular-nums dark:text-zinc-100">{value}</div>
    </div>
  );
};

export default function WaitlistPanel({ businessId }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyKey, setBusyKey] = useState("");
  const [toast, setToast] = useState("");
  const [adding, setAdding] = useState(false);
  const [notifying, setNotifying] = useState(null); // entry being notified

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  async function loadAll() {
    setLoading(true);
    try {
      const [list, st, biz, cust] = await Promise.all([
        api.get(`/businesses/${businessId}/waitlist`),
        api.get(`/businesses/${businessId}/waitlist/stats`),
        api.get(`/businesses/${businessId}`),
        api.get(`/businesses/${businessId}/customers`),
      ]);
      setRows(list.data || []);
      setStats(st.data);
      setServices(biz.data?.services || []);
      setCustomers(
        (cust.data || []).filter(
          (c) => c.customer_id &&
            !c.customer_id.startsWith("walkin-") &&
            !c.customer_id.startsWith("guest-") &&
            !c.customer_id.startsWith("deleted-"),
        ),
      );
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (businessId) loadAll(); /* eslint-disable-next-line */ }, [businessId]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  async function deleteEntry(wid) {
    if (!window.confirm("Rimuovere questa iscrizione dalla lista d'attesa?")) return;
    setBusyKey(`del-${wid}`);
    try {
      await api.delete(`/waitlist/${wid}`);
      showToast("Rimosso ✓");
      loadAll();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    } finally {
      setBusyKey("");
    }
  }

  return (
    <div data-testid="waitlist-panel">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-medium tracking-tight inline-flex items-center gap-2 dark:text-zinc-100">
            <Hourglass className="h-5 w-5 text-blue-600" /> Lista d'attesa
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl dark:text-zinc-400">
            I clienti iscritti vengono avvisati automaticamente via email + push quando si libera uno slot
            che combacia con le loro preferenze (data, giorno, fascia oraria).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadAll} className="h-10 w-10 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="waitlist-refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={() => setAdding(true)} className="btn-accent h-10 px-4 text-sm" data-testid="waitlist-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi label="Totali" value={stats.total} icon={Hourglass} accent="zinc" />
          <Kpi label="In attesa" value={stats.waiting} icon={Clock4} accent="amber" />
          <Kpi label="Notificati" value={stats.notified} icon={MailCheck} accent="blue" />
          <Kpi label="Prenotati" value={stats.booked} icon={CheckCircle2} accent="emerald" />
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar">
        {[
          { k: "all", label: `Tutti (${rows.length})` },
          { k: "waiting", label: `In attesa (${rows.filter((r) => r.status === "waiting").length})` },
          { k: "notified", label: `Notificati (${rows.filter((r) => r.status === "notified").length})` },
          { k: "booked", label: `Prenotati (${rows.filter((r) => r.status === "booked").length})` },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap ${filter === f.k ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"}`}
            data-testid={`waitlist-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="card-soft p-10 text-center" data-testid="waitlist-empty">
          <Hourglass className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
          <div className="font-display text-lg font-medium dark:text-zinc-100">
            {rows.length === 0 ? "Nessun cliente in lista d'attesa" : "Nessun risultato per il filtro"}
          </div>
          <div className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">
            {rows.length === 0
              ? "I clienti potranno iscriversi dalla pagina di prenotazione quando non trovano slot disponibili."
              : "Cambia filtro per vedere altre iscrizioni."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const st = STATUS[r.status] || STATUS.waiting;
            return (
              <div key={r.id} className={`relative rounded-2xl border ${st.border} ${st.bg} p-4`} data-testid={`waitlist-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${st.text}`}>
                        <span className={`h-2 w-2 rounded-full ${st.dot}`} /> {st.label}
                      </span>
                      <span className="text-xs text-zinc-400 dark:text-zinc-500">·</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">iscritto {fmtDate(r.created_at)}</span>
                      {r.notification_count > 0 && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-100 rounded-full px-2 py-0.5 dark:text-blue-300 dark:bg-blue-950/50">
                          {r.notification_count} notifiche
                        </span>
                      )}
                      {r.source === "customer" && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5 dark:text-zinc-400 dark:bg-zinc-800">
                          Auto-iscritto
                        </span>
                      )}
                    </div>
                    <div className="font-display text-lg font-medium mt-1 truncate dark:text-zinc-100">
                      {r.customer_name} <span className="text-zinc-500 font-normal dark:text-zinc-400">· {r.service_name}</span>
                    </div>
                    <div className="text-xs text-zinc-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 dark:text-zinc-400">
                      <span>{r.customer_email}</span>
                      {r.customer_phone && <span>· {r.customer_phone}</span>}
                    </div>
                    <div className="text-xs text-zinc-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 dark:text-zinc-400">
                      <span>
                        <strong>Periodo:</strong> {r.preferred_date_from ? fmtDate(r.preferred_date_from) : "qualsiasi"} — {r.preferred_date_to ? fmtDate(r.preferred_date_to) : "qualsiasi"}
                      </span>
                      {r.preferred_dows?.length > 0 && (
                        <span><strong>Giorni:</strong> {r.preferred_dows.map((d) => WEEKDAYS_IT[d]).join(", ")}</span>
                      )}
                      {(r.preferred_time_from_min != null || r.preferred_time_to_min != null) && (
                        <span><strong>Orario:</strong> {minToHHMM(r.preferred_time_from_min) || "—"}–{minToHHMM(r.preferred_time_to_min) || "—"}</span>
                      )}
                    </div>
                    {r.notes && (
                      <div className="text-xs text-zinc-700 mt-2 bg-white/60 rounded-lg px-3 py-2 border border-zinc-200/60 dark:text-zinc-300 dark:bg-zinc-900/60 dark:border-zinc-700/60">
                        <span className="text-zinc-500 font-medium dark:text-zinc-400">Note: </span>{r.notes}
                      </div>
                    )}
                    {r.last_proposal_start_at && (
                      <div className="text-xs text-blue-700 mt-2 dark:text-blue-300">
                        Ultima proposta inviata per: <strong>{fmtDt(r.last_proposal_start_at)}</strong>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {r.status !== "booked" && (
                      <button
                        onClick={() => setNotifying(r)}
                        className="h-9 px-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`waitlist-notify-${r.id}`}
                      >
                        <Send className="h-3.5 w-3.5" /> Proponi slot
                      </button>
                    )}
                    <button
                      onClick={() => deleteEntry(r.id)}
                      disabled={busyKey === `del-${r.id}`}
                      className="h-9 px-4 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50 dark:border-rose-900/60 dark:text-rose-300 dark:hover:bg-rose-950/40 dark:bg-rose-950/40"
                      data-testid={`waitlist-delete-${r.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Elimina
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <WaitlistAddModal
          businessId={businessId}
          services={services}
          customers={customers}
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); showToast("Aggiunto alla lista d'attesa ✓"); loadAll(); }}
        />
      )}

      {notifying && (
        <NotifyModal
          entry={notifying}
          onClose={() => setNotifying(null)}
          onSent={() => { setNotifying(null); showToast("Proposta inviata ✓"); loadAll(); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-full shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

function WaitlistAddModal({ businessId, services, customers, onClose, onCreated }) {
  const [customerId, setCustomerId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceId, setServiceId] = useState(services[0]?.id || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dows, setDows] = useState([]);
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  function pickCustomer(cid) {
    setCustomerId(cid);
    const c = customers.find((x) => x.customer_id === cid);
    if (c) {
      setName(c.customer_name || "");
      setEmail(c.customer_email || "");
      setPhone(c.customer_phone || "");
    }
  }

  function toggleDow(d) {
    setDows((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!serviceId) return setErr("Seleziona un servizio");
    if (!name.trim() || !email.trim()) return setErr("Nome ed email sono obbligatori");
    setBusy(true);
    try {
      await api.post(`/businesses/${businessId}/waitlist`, {
        customer_id: customerId || null,
        customer_name: name.trim(),
        customer_email: email.trim().toLowerCase(),
        customer_phone: phone.trim() || null,
        service_id: serviceId,
        preferred_date_from: dateFrom || null,
        preferred_date_to: dateTo || null,
        preferred_dows: dows,
        preferred_time_from_min: HHMMtoMin(timeFrom),
        preferred_time_to_min: HHMMtoMin(timeTo),
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="waitlist-add-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto card-soft dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Lista d'attesa</div>
              <h2 className="font-display text-2xl font-medium tracking-tight dark:text-zinc-100">Aggiungi cliente</h2>
            </div>
            <button type="button" onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Cliente esistente (opzionale)</label>
            <select value={customerId} onChange={(e) => pickCustomer(e.target.value)} className="input-field" data-testid="waitlist-add-customer">
              <option value="">— Nuovo / non in archivio —</option>
              {customers.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>{c.customer_name} · {c.customer_email}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Nome *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="input-field" data-testid="waitlist-add-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" data-testid="waitlist-add-email" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Telefono</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-field" data-testid="waitlist-add-phone" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Servizio *</label>
            <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} className="input-field" data-testid="waitlist-add-service">
              {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_min}min · €{s.price}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Da (data)</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">A (data)</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-field" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 block dark:text-zinc-400">Giorni preferiti (opz.)</label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS_IT.map((d, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => toggleDow(i)}
                  className={`h-9 px-3 rounded-full text-xs font-medium border transition ${dows.includes(i) ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Dalle ore</label>
              <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Alle ore</label>
              <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} className="input-field" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Note (opz.)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-field" />
          </div>

          {err && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 inline-flex items-center gap-2 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-300">
              <AlertTriangle className="h-4 w-4" /> {err}
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-accent w-full" data-testid="waitlist-add-submit">
            {busy ? "Salvataggio…" : "Aggiungi alla lista d'attesa"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NotifyModal({ entry, onClose, onSent }) {
  // Owner picks a slot to propose to the waitlist customer.
  // Default value: today + tomorrow morning at 10:00 if no preferred range.
  const defaultDt = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  const [when, setWhen] = useState(defaultDt);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      // Send local datetime + tz offset so the backend treats it as Europe/Rome.
      const d = new Date(when);
      const off = -d.getTimezoneOffset();
      const sign = off >= 0 ? "+" : "-";
      const pad = (n) => String(n).padStart(2, "0");
      const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(Math.abs(Math.floor(off / 60)))}:${pad(Math.abs(off % 60))}`;
      await api.post(`/waitlist/${entry.id}/notify?start_at=${encodeURIComponent(iso)}`);
      onSent();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="waitlist-notify-modal">
      <div className="bg-white rounded-3xl w-full max-w-md card-soft dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Proponi slot</div>
              <h2 className="font-display text-2xl font-medium tracking-tight dark:text-zinc-100">{entry.customer_name}</h2>
              <p className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">{entry.service_name} · email + push al cliente con link 1-click (scade in 48h)</p>
            </div>
            <button type="button" onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Data e ora proposta</label>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="input-field" required data-testid="waitlist-notify-when" />
          </div>

          {err && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 inline-flex items-center gap-2 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-300">
              <AlertTriangle className="h-4 w-4" /> {err}
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-accent w-full" data-testid="waitlist-notify-submit">
            {busy ? "Invio…" : "Invia proposta"}
          </button>
        </form>
      </div>
    </div>
  );
}
